import cn from 'classnames';
import React, {forwardRef} from 'react';

import AemEditableText from '@/components/Aem/AemEditableText/AemEditableText';
import BackButton from '@/components/BackButton/BackButton';
import {ButtonRaw} from '@/components/Button';
import DeviceIcon from '@/components/DeviceIcon/DeviceIcon';
import {CancelIcon, ChevronRightIcon} from '@/components/Icons';
import PopupStickedWithSwipeClose from '@/components/Popup/PopupStickedWithSwipeClose';
import {CLOSE_BUTTON_ARIA_LABEL} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import useIsDesktopLayout from '@/hooks/layout/useIsDesktopLayout';
import {getAriaLabel} from '@/services/accessibilityService';
import routerService from '@/services/route/routerService';
import {twx} from '@/utils/tailwindUtils';

import WithSettingsGroupButtonAriaLabel from './WithGroupButtonAriaLabel/WithSettingsGroupButtonAriaLabel';

const DevicesSettingsSection = () => null;

const Container = forwardRef(
    ({children, className, title, onClick, pageName, itemName, isContentContainerFocusable, ...rest}, ref) => {
        const isDesktop = useIsDesktopLayout();

        if (isDesktop) {
            return (
                <div ref={ref} className={twx('ml:px-8 ml:pt-8', className)} {...rest}>
                    <BackButton />
                    {children}
                </div>
            );
        }

        return (
            <PopupStickedWithSwipeClose
                onClose={routerService.goBack}
                className='flex flex-col overflow-auto overflow-x-hidden rounded-t-xl bg-romance px-6 pt-[calc(env(safe-area-inset-top)+var(--hw-h))] pb-safe-offset-8 ml:overflow-hidden'
                swipeAreaClassName='py-4'
                isSwipeDashVisible={false}
                contentRef={ref}
                {...rest}
            >
                <div className='mb-4 flex w-full justify-end'>
                    <ButtonRaw
                        className='ml-0 shrink-0'
                        aria-label={getAriaLabel(CLOSE_BUTTON_ARIA_LABEL)}
                        onClick={routerService.goBack}
                    >
                        <CancelIcon />
                    </ButtonRaw>
                </div>
                {children}
            </PopupStickedWithSwipeClose>
        );
    }
);

const Group = ({children, className}) => {
    return <div className={twx('px-4 md:px-8', className)}>{children}</div>;
};

const GroupTitle = ({itemName}) => {
    return (
        <AemEditableText
            pageName={routePaths.DEVICES_PAGE}
            itemName={itemName}
            render={(text) =>
                text && (
                    <h2 className='mb-4 text-20 leading-12 tracking-02 md:text-20 ml:mb-6 ml:text-24 ml:leading-15 ml:tracking-normal'>
                        {text}
                    </h2>
                )
            }
        />
    );
};

const GroupButtons = ({className, children}) => {
    return <div className={twx('rounded-xl bg-romance ml:bg-primary-dark-5', className)}>{children}</div>;
};

const GroupButton = ({
    className,
    textClassName,
    mode,
    icon: Icon,
    itemName,
    subItemName,
    hasSeparator,
    onClick,
    hideArrow,
    ariaLabelKey,
}) => {
    return (
        <>
            <WithSettingsGroupButtonAriaLabel
                ariaLabelKey={ariaLabelKey}
                subItemName={subItemName}
                renderContent={(ariaLabel) => (
                    <ButtonRaw
                        className={cn(className, 'flex w-full flex-col justify-between px-4 pt-4')}
                        aria-label={ariaLabel}
                        onClick={onClick}
                    >
                        <div className='flex w-full items-center justify-between gap-2 pb-4'>
                            {mode && <DeviceIcon className='shrink-0' mode={mode} />}
                            {Icon && <Icon className='h-12 w-12 shrink-0' />}
                            <div className='mr-auto flex flex-col pr-1'>
                                <span
                                    className={twx(
                                        'text-14 font-bold leading-143 tracking-02 ml:font-normal ml:tracking-04',
                                        textClassName
                                    )}
                                >
                                    <AemEditableText pageName={routePaths.DEVICES_PAGE} itemName={itemName} />
                                </span>
                                {subItemName && (
                                    <span className='text-14 font-bold tracking-02'>
                                        <AemEditableText pageName={routePaths.DEVICES_PAGE} itemName={subItemName} />
                                    </span>
                                )}
                            </div>
                            {!hideArrow && <ChevronRightIcon className='shrink-0' />}
                        </div>
                    </ButtonRaw>
                )}
            />
            {hasSeparator && <ButtonSeparator />}
        </>
    );
};

const ButtonSeparator = () => {
    return <hr className='mx-4 h-[1px] bg-primary-dark-15' aria-hidden='true' />;
};

DevicesSettingsSection.Container = Container;
DevicesSettingsSection.Group = Group;
DevicesSettingsSection.Title = GroupTitle;
DevicesSettingsSection.Buttons = GroupButtons;
DevicesSettingsSection.Button = GroupButton;

export default DevicesSettingsSection;
